import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { facebookAdSetsService, CreateAdSetPayload } from '@/services/facebook-ad-sets.service';
import { facebookCampaignsService, FbCampaign } from '@/services/facebook-campaigns.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { Check, Circle, ChevronRight, Users, DollarSign, ClipboardList, Globe, AlertCircle } from 'lucide-react';
import { CampaignStatusPill } from '@/components/facebook/CampaignStatusPill';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  optGoalsForObjective,
  goalLabel,
  budgetFloorFor,
  checkPlacementCascade,
  ADVANTAGE_AUDIENCE_AGE_MAX,
  DESTINATION_TYPES,
  PLACEMENT_REQUIRES_FEED,
  type OdaxObjective,
} from '@/lib/meta-matrix';

const COUNTRIES = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'IN', label: 'India' },
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'EG', label: 'Egypt' },
  { code: 'KW', label: 'Kuwait' },
  { code: 'QA', label: 'Qatar' },
  { code: 'BH', label: 'Bahrain' },
  { code: 'OM', label: 'Oman' },
];

// PAGE_LIKES removed — deprecated in ODAX v24 (Meta rejects the combo).
const BILLING_EVENTS = ['IMPRESSIONS', 'LINK_CLICKS', 'POST_ENGAGEMENT', 'VIDEO_VIEWS', 'APP_INSTALLS', 'THRUPLAY'];

const DEST_LABELS: Record<string, string> = {
  WEBSITE: 'Website',
  APP: 'App',
  MESSENGER: 'Messenger',
  WHATSAPP: 'WhatsApp',
  INSTAGRAM_DIRECT: 'Instagram Direct',
  ON_AD: 'On the ad',
  ON_POST: 'On a post',
  ON_PAGE: 'On a page',
  ON_EVENT: 'On an event',
  ON_VIDEO: 'On a video',
};

const STEPS = [
  { label: 'Basics', icon: <Globe size={15} /> },
  { label: 'Targeting', icon: <Users size={15} /> },
  { label: 'Budget', icon: <DollarSign size={15} /> },
  { label: 'Review', icon: <ClipboardList size={15} /> },
];

export default function AdSetBuilder() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const nav = useNavigate();
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [campaign, setCampaign] = useState<FbCampaign | null>(null);
  const [step, setStep] = useState(0);

  // Step 1 — Basics
  const [name, setName] = useState('');
  const [billingEvent, setBillingEvent] = useState('IMPRESSIONS');
  const [optimizationGoal, setOptimizationGoal] = useState('LINK_CLICKS');
  const [destinationType, setDestinationType] = useState<string>('WEBSITE');

  // Step 2 — Targeting
  const [countries, setCountries] = useState<string[]>(['AE']);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(55);
  const [genders, setGenders] = useState<number[]>([]);
  const [advantageAudience, setAdvantageAudience] = useState<0 | 1>(0);
  /** When 'auto', Meta picks every placement; when 'manual', the user picks. */
  const [placementMode, setPlacementMode] = useState<'auto' | 'manual'>('auto');
  const [fbPositions, setFbPositions] = useState<string[]>(['feed']);
  const [igPositions, setIgPositions] = useState<string[]>(['stream']);
  const [messengerPositions, setMessengerPositions] = useState<string[]>([]);
  const [anPositions, setAnPositions] = useState<string[]>([]);

  // Step 3 — Budget & schedule
  const [budgetMode, setBudgetMode] = useState<'daily' | 'lifetime'>('daily');
  const [budgetMajor, setBudgetMajor] = useState('20');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'PAUSED'>('PAUSED');

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
    if (campaignId) facebookCampaignsService.get(campaignId).then(setCampaign).catch(() => undefined);
  }, [campaignId]);

  // Filter optimization goals by the parent campaign's objective. When the
  // campaign hasn't loaded yet, fall back to the Traffic list — the most
  // common default. Once campaign loads, narrow down + auto-fix the selection
  // if it's no longer valid.
  const campaignObjective = campaign?.objective as OdaxObjective | undefined;
  const validGoals = useMemo(
    () => (campaignObjective ? optGoalsForObjective(campaignObjective) : ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'IMPRESSIONS', 'REACH']),
    [campaignObjective],
  );

  // If the campaign loads and the current goal isn't valid for its objective,
  // auto-reset to the first valid goal so the user can't submit an invalid combo.
  useEffect(() => {
    if (validGoals.length > 0 && !validGoals.includes(optimizationGoal)) {
      setOptimizationGoal(validGoals[0]);
    }
  }, [validGoals, optimizationGoal]);

  // Advantage+ Audience constraints — Meta enforces age_max=65 when on.
  useEffect(() => {
    if (advantageAudience === 1 && ageMax < ADVANTAGE_AUDIENCE_AGE_MAX) {
      setAgeMax(ADVANTAGE_AUDIENCE_AGE_MAX);
    }
  }, [advantageAudience, ageMax]);

  /**
   * Auto-enable required peer placements when the user picks a sub-placement
   * that requires Feed (or another anchor). See PLACEMENT_REQUIRES_FEED for
   * the matrix. This eliminates the most common Meta error class —
   * "FB Stories needs FB Feed or IG Stories" etc.
   */
  useEffect(() => {
    if (placementMode !== 'manual') return;
    const targeting = {
      facebook_positions: fbPositions,
      instagram_positions: igPositions,
      messenger_positions: messengerPositions,
      audience_network_positions: anPositions,
    };
    const violation = checkPlacementCascade(targeting);
    if (!violation) return;
    // Auto-tick the FIRST requirement (we choose the safest default — Feed)
    const firstReq = violation.requires[0];
    if (!firstReq) return;
    const [arrName, pos] = firstReq.split(':');
    if (arrName === 'facebook_positions' && !fbPositions.includes(pos)) {
      setFbPositions(prev => [...prev, pos]);
    } else if (arrName === 'instagram_positions' && !igPositions.includes(pos)) {
      setIgPositions(prev => [...prev, pos]);
    } else if (arrName === 'messenger_positions' && !messengerPositions.includes(pos)) {
      setMessengerPositions(prev => [...prev, pos]);
    } else if (arrName === 'audience_network_positions' && !anPositions.includes(pos)) {
      setAnPositions(prev => [...prev, pos]);
    }
  }, [placementMode, fbPositions, igPositions, messengerPositions, anPositions]);

  // Publisher platforms inferred from which arrays are non-empty.
  const publisherPlatforms = useMemo(() => {
    const platforms: string[] = [];
    if (fbPositions.length > 0) platforms.push('facebook');
    if (igPositions.length > 0) platforms.push('instagram');
    if (messengerPositions.length > 0) platforms.push('messenger');
    if (anPositions.length > 0) platforms.push('audience_network');
    return platforms;
  }, [fbPositions, igPositions, messengerPositions, anPositions]);

  const toggleCountry = (code: string) => {
    setCountries((c) => (c.includes(code) ? c.filter((x) => x !== code) : [...c, code]));
  };

  const toggleGender = (g: number) => {
    setGenders((gs) => (gs.includes(g) ? gs.filter((x) => x !== g) : [...gs, g]));
  };

  // Meta reports the account currency; until it does, show '—' rather than guess.
  const currency = account?.currency ?? '';

  // Daily budget in minor units, used to check Meta's destination-specific floor.
  const budgetMinor = useMemo(() => {
    const n = parseFloat(budgetMajor || '0');
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [budgetMajor]);
  const requiredFloor = budgetFloorFor(destinationType);
  const budgetBelowFloor = budgetMode === 'daily' && budgetMinor > 0 && budgetMinor < requiredFloor;

  // WhatsApp destination requires a linked WABA on the ad account.
  const whatsappBlocked = destinationType === 'WHATSAPP' && !(account as MetaAdAccount & { whatsappBusinessAccountId?: string })?.whatsappBusinessAccountId;

  const stepValid = (s: number) => {
    if (s === 0) {
      if (!name) return false;
      if (whatsappBlocked) return false;
      return true;
    }
    if (s === 1) return countries.length > 0;
    if (s === 2) return !!budgetMajor && parseFloat(budgetMajor) > 0 && !budgetBelowFloor;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const major = parseFloat(budgetMajor || '0');
      if (isNaN(major) || major <= 0) {
        toast.error('Enter a valid budget');
        setBusy(false);
        return;
      }
      const minor = String(Math.round(major * 100));
      const payload: CreateAdSetPayload = {
        name,
        campaign_id: campaignId!,
        status,
        billing_event: billingEvent,
        optimization_goal: optimizationGoal,
        destination_type: destinationType,
        targeting: {
          countries,
          age_min: ageMin,
          age_max: ageMax,
          genders: genders.length === 0 ? undefined : genders,
          advantage_audience: advantageAudience,
          // Placements: only send when user explicitly chose manual mode.
          // Auto mode omits these so Meta uses Advantage+ Placements.
          ...(placementMode === 'manual' ? {
            publisher_platforms: publisherPlatforms,
            facebook_positions: fbPositions.length > 0 ? fbPositions : undefined,
            instagram_positions: igPositions.length > 0 ? igPositions : undefined,
            messenger_positions: messengerPositions.length > 0 ? messengerPositions : undefined,
            audience_network_positions: anPositions.length > 0 ? anPositions : undefined,
          } : {}),
        },
      };
      if (budgetMode === 'daily') payload.daily_budget = minor;
      else payload.lifetime_budget = minor;
      if (startTime) payload.start_time = new Date(startTime).toISOString();
      if (endTime) payload.end_time = new Date(endTime).toISOString();

      const r = await facebookAdSetsService.create(payload);
      toast.success(`Ad Set created · status ${status}`);
      nav(`/facebook/ad-sets/${r.id}`);
    } catch {
      /* toast shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Topbar
        title="New Ad Set"
        breadcrumb={`Facebook Ads / Campaigns / ${campaign?.name ?? '…'} / New Ad Set`}
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav(`/facebook/campaigns/${campaignId}`)}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                disabled={!stepValid(step)}
                onClick={() => setStep((s) => s + 1)}
                iconRight={<ChevronRight size={14} />}
              >
                Next
              </Button>
            ) : (
              <Button loading={busy} onClick={submit}>
                Create ad set
              </Button>
            )}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-5">
        {/* Stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2">
              <button
                onClick={() => { if (i < step || stepValid(i)) setStep(i); }}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                  i < step
                    ? 'bg-gold-gradient text-on-primary shadow-soft'
                    : i === step
                    ? 'border border-primary bg-primary-soft text-primary'
                    : 'border border-border text-text-muted',
                )}
              >
                {i < step ? <Check size={13} /> : i === step ? s.icon : <Circle size={13} />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && (
                <div className={clsx('h-px w-6 transition-colors', i < step ? 'bg-primary' : 'bg-border')} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 — Basics */}
        {step === 0 && (
          <Card>
            <SectionHeader title="Ad set basics" icon={<Globe size={16} />} />
            <FormField label="Ad set name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="UAE Adults 25–45"
              />
            </FormField>
            <FormField
              label="Conversion location"
              hint="Where do you want people to take action?"
              required
            >
              <Select value={destinationType} onChange={(e) => setDestinationType(e.target.value)}>
                {DESTINATION_TYPES.map((d) => (
                  <option key={d} value={d}>{DEST_LABELS[d] ?? d}</option>
                ))}
              </Select>
            </FormField>
            {whatsappBlocked && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <strong>WhatsApp not configured.</strong> Connect a WhatsApp Business Account in
                  Ad Account Settings → WhatsApp before using this destination.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Billing event" required>
                <Select value={billingEvent} onChange={(e) => setBillingEvent(e.target.value)}>
                  {BILLING_EVENTS.map((b) => (
                    <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>
                  ))}
                </Select>
              </FormField>
              <FormField
                label="Optimization goal"
                hint={campaignObjective ? `Filtered by campaign objective: ${campaignObjective.replace('OUTCOME_', '')}` : undefined}
                required
              >
                <Select value={optimizationGoal} onChange={(e) => setOptimizationGoal(e.target.value)}>
                  {validGoals.map((g) => (
                    <option key={g} value={g}>{goalLabel(g)}</option>
                  ))}
                </Select>
              </FormField>
            </div>
          </Card>
        )}

        {/* Step 2 — Targeting */}
        {step === 1 && (
          <Card>
            <SectionHeader title="Targeting" icon={<Users size={16} />} />
            <FormField label="Countries" required>
              <div className="flex flex-wrap gap-2 mt-1">
                {COUNTRIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggleCountry(c.code)}
                    className={clsx(
                      'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                      countries.includes(c.code)
                        ? 'bg-gold-gradient text-on-primary shadow-soft'
                        : 'border border-border bg-surface-alt text-text-muted hover:bg-surface hover:border-border-strong',
                    )}
                  >
                    {c.code} · {c.label}
                  </button>
                ))}
              </div>
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Age min">
                <Input
                  type="number"
                  min="13"
                  max="65"
                  value={ageMin}
                  onChange={(e) => setAgeMin(parseInt(e.target.value, 10) || 18)}
                />
              </FormField>
              <FormField label="Age max">
                <Input
                  type="number"
                  min="13"
                  max="65"
                  value={ageMax}
                  onChange={(e) => setAgeMax(parseInt(e.target.value, 10) || 65)}
                />
              </FormField>
            </div>
            <FormField label="Genders" hint="Leave blank to target all">
              <div className="flex gap-2 mt-1">
                {[{ g: 1, label: 'Male' }, { g: 2, label: 'Female' }].map(({ g, label }) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGender(g)}
                    className={clsx(
                      'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors',
                      genders.includes(g)
                        ? 'bg-gold-gradient text-on-primary shadow-soft'
                        : 'border border-border bg-surface-alt text-text-muted hover:bg-surface hover:border-border-strong',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField
              label="Advantage+ Audience"
              hint={
                advantageAudience === 1
                  ? `When ON, Meta enforces age_max=${ADVANTAGE_AUDIENCE_AGE_MAX}`
                  : 'Let Meta find people likely to engage beyond your defined targeting'
              }
            >
              <div className="flex gap-2 mt-1">
                {[{ v: 0, label: 'Off' }, { v: 1, label: 'On' }].map(({ v, label }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAdvantageAudience(v as 0 | 1)}
                    className={clsx(
                      'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors',
                      advantageAudience === v
                        ? 'bg-gold-gradient text-on-primary shadow-soft'
                        : 'border border-border bg-surface-alt text-text-muted hover:bg-surface hover:border-border-strong',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FormField>

            {/* Placements — Advantage+ by default; manual unlocks cascade-aware picker */}
            <FormField
              label="Placements"
              hint={placementMode === 'auto'
                ? 'Advantage+ Placements — Meta picks the surfaces likely to perform best'
                : 'Manual picker. Required peer placements are auto-ticked.'}
            >
              <div className="flex gap-2 mt-1 mb-3">
                {[{ v: 'auto', label: 'Advantage+ (recommended)' }, { v: 'manual', label: 'Manual' }].map(({ v, label }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setPlacementMode(v as 'auto' | 'manual')}
                    className={clsx(
                      'flex-1 rounded-lg border py-2 text-xs font-semibold transition-all',
                      placementMode === v
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border bg-surface text-text-muted hover:bg-surface-alt',
                    )}
                  >{label}</button>
                ))}
              </div>
              {placementMode === 'manual' && (
                <PlacementPicker
                  fbPositions={fbPositions} setFbPositions={setFbPositions}
                  igPositions={igPositions} setIgPositions={setIgPositions}
                  messengerPositions={messengerPositions} setMessengerPositions={setMessengerPositions}
                  anPositions={anPositions} setAnPositions={setAnPositions}
                />
              )}
            </FormField>
          </Card>
        )}

        {/* Step 3 — Budget */}
        {step === 2 && (
          <Card>
            <SectionHeader title="Budget & schedule" icon={<DollarSign size={16} />} />
            <div className="mb-4 flex gap-3">
              {(['daily', 'lifetime'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setBudgetMode(mode)}
                  className={clsx(
                    'flex-1 rounded-lg border py-3 text-sm font-semibold transition-all',
                    budgetMode === mode
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-muted hover:bg-surface-alt',
                  )}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)} budget
                </button>
              ))}
            </div>
            <FormField
              label={`Amount (${currency || '—'})`}
              required
              hint={
                ['WHATSAPP', 'MESSENGER'].includes(destinationType)
                  ? `Min ${(requiredFloor / 100).toFixed(2)} ${currency} / day required for ${DEST_LABELS[destinationType]} destination`
                  : undefined
              }
            >
              <Input
                type="number"
                min="1"
                step="0.01"
                value={budgetMajor}
                onChange={(e) => setBudgetMajor(e.target.value)}
                placeholder="20.00"
              />
            </FormField>
            {budgetBelowFloor && (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  Budget is below Meta's floor for <strong>{DEST_LABELS[destinationType]}</strong> destination.
                  Increase to at least {(requiredFloor / 100).toFixed(2)} {currency} / day.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Start time (optional)">
                <Input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </FormField>
              <FormField label="End time (optional)">
                <Input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </FormField>
            </div>
            <FormField label="Initial status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'PAUSED')}>
                <option value="PAUSED">Paused — review before activating</option>
                <option value="ACTIVE">Active — go live immediately</option>
              </Select>
            </FormField>
          </Card>
        )}

        {/* Step 4 — Review */}
        {step === 3 && (
          <Card>
            <SectionHeader title="Review & create" icon={<ClipboardList size={16} />} />
            <div className="divide-y divide-border">
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Name</div>
                <div className="font-semibold text-text-primary">{name}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Billing event</div>
                <div className="font-semibold text-text-primary">{billingEvent.replace(/_/g, ' ')}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Optimization goal</div>
                <div className="font-semibold text-text-primary">{optimizationGoal.replace(/_/g, ' ')}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Countries</div>
                <div className="font-semibold text-text-primary">{countries.join(', ')}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Ages</div>
                <div className="tabular-nums font-semibold text-text-primary">{ageMin}–{ageMax}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Genders</div>
                <div className="font-semibold text-text-primary">
                  {genders.length === 0 ? 'All' : genders.map((g) => (g === 1 ? 'Male' : 'Female')).join(', ')}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Budget</div>
                <div className="tabular-nums font-semibold text-text-primary">
                  {currency} {parseFloat(budgetMajor || '0').toFixed(2)} / {budgetMode}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Status</div>
                <div><CampaignStatusPill status={status} /></div>
              </div>
            </div>
          </Card>
        )}

        {/* Bottom nav */}
        <div className="flex justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
          {step < 3 ? (
            <Button
              disabled={!stepValid(step)}
              onClick={() => setStep((s) => s + 1)}
              iconRight={<ChevronRight size={14} />}
            >
              Next: {STEPS[step + 1].label}
            </Button>
          ) : (
            <Button loading={busy} onClick={submit}>
              Create ad set
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Placement picker ────────────────────────────────────────────────────────
// Cascade is enforced in two places:
//   1. Auto-enable peer placements via useEffect at the top of AdSetBuilder
//      (when user picks Stories, Feed auto-ticks).
//   2. Visible inline warning if the current selection still violates a rule
//      (this can happen mid-toggle before the auto-enable fires).

const FB_POSITIONS = [
  { id: 'feed', label: 'Feed' },
  { id: 'facebook_reels', label: 'Reels' },
  { id: 'story', label: 'Stories' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'video_feeds', label: 'Video feeds' },
  { id: 'instream_video', label: 'In-stream video' },
  { id: 'search', label: 'Search' },
  { id: 'right_hand_column', label: 'Right column' },
  { id: 'biz_disco_feed', label: 'Business Explore' },
];
const IG_POSITIONS = [
  { id: 'stream', label: 'Feed' },
  { id: 'story', label: 'Stories' },
  { id: 'reels', label: 'Reels' },
  { id: 'explore', label: 'Explore' },
  { id: 'explore_home', label: 'Explore Home' },
  { id: 'shop', label: 'Shop' },
  { id: 'profile_feed', label: 'Profile feed' },
  { id: 'ig_search', label: 'Search' },
];
const MESSENGER_POSITIONS = [
  { id: 'messenger_home', label: 'Inbox' },
  { id: 'story', label: 'Stories' },
];
const AN_POSITIONS = [
  { id: 'classic', label: 'Classic' },
  { id: 'rewarded_video', label: 'Rewarded video' },
];

interface PlacementPickerProps {
  fbPositions: string[]; setFbPositions: (v: string[]) => void;
  igPositions: string[]; setIgPositions: (v: string[]) => void;
  messengerPositions: string[]; setMessengerPositions: (v: string[]) => void;
  anPositions: string[]; setAnPositions: (v: string[]) => void;
}

const PlacementPicker: React.FC<PlacementPickerProps> = ({
  fbPositions, setFbPositions,
  igPositions, setIgPositions,
  messengerPositions, setMessengerPositions,
  anPositions, setAnPositions,
}) => {
  const violation = useMemo(() => checkPlacementCascade({
    facebook_positions: fbPositions,
    instagram_positions: igPositions,
    messenger_positions: messengerPositions,
    audience_network_positions: anPositions,
  }), [fbPositions, igPositions, messengerPositions, anPositions]);

  const toggle = (set: (v: string[]) => void, current: string[], id: string) => {
    set(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  };

  const Section: React.FC<{ title: string; options: { id: string; label: string }[]; selected: string[]; onToggle: (id: string) => void }> = ({
    title, options, selected, onToggle,
  }) => (
    <div>
      <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-text-muted">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            className={clsx(
              'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
              selected.includes(o.id)
                ? 'bg-gold-gradient text-on-primary shadow-soft'
                : 'border border-border bg-surface text-text-muted hover:bg-surface-alt',
            )}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <Section title="Facebook" options={FB_POSITIONS} selected={fbPositions} onToggle={(id) => toggle(setFbPositions, fbPositions, id)} />
      <Section title="Instagram" options={IG_POSITIONS} selected={igPositions} onToggle={(id) => toggle(setIgPositions, igPositions, id)} />
      <Section title="Messenger" options={MESSENGER_POSITIONS} selected={messengerPositions} onToggle={(id) => toggle(setMessengerPositions, messengerPositions, id)} />
      <Section title="Audience Network" options={AN_POSITIONS} selected={anPositions} onToggle={(id) => toggle(setAnPositions, anPositions, id)} />

      {violation && (
        <div className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <span>{violation.description}. Auto-enabling required peer…</span>
        </div>
      )}

      <p className="text-2xs text-text-muted">
        {PLACEMENT_REQUIRES_FEED.length} cascade rules enforced — picking a sub-placement (Stories, Marketplace, AN, etc.) auto-ticks its Feed dependency.
      </p>
    </div>
  );
};
